import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { ROLES } from "../lib/format";
import { Building2, ChevronDown, Plus, Layers, Pencil } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from "./ui/dropdown-menu";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { toast } from "sonner";

export default function CompanySwitcher() {
  const { isRole } = useAuth();
  const [companies, setCompanies] = useState([]);
  const [active, setActive] = useState(localStorage.getItem("stems_company") || "");
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [manageOpen, setManageOpen] = useState(false);
  const [edits, setEdits] = useState({});
  const [savingId, setSavingId] = useState(null);

  const load = () => api.get("/companies").then(({ data }) => {
    setCompanies(data);
    // One-time self-heal: company-wide roles (FM/GM/ACEO) default to the "All business units" view
    // so they never land on an empty single-unit roster and wrongly see "No employees yet".
    const canAll = isRole(ROLES.FM) || isRole(ROLES.GM) || isRole(ROLES.ACEO);
    if (canAll && data.length > 1 && !localStorage.getItem("stems_unit_default_v2")) {
      localStorage.setItem("stems_unit_default_v2", "1");
      localStorage.setItem("stems_company", "all");
      setActive("all");
      window.location.reload();
      return;
    }
    if (!localStorage.getItem("stems_company") && data[0]) {
      localStorage.setItem("stems_company", data[0].id);
      setActive(data[0].id);
    }
  });
  useEffect(() => { load(); }, []);

  const pick = (id) => {
    localStorage.setItem("stems_company", id);
    setActive(id);
    window.location.reload();
  };
  const create = async () => {
    if (!name.trim()) return;
    try {
      const { data } = await api.post("/companies", { name });
      toast.success("Company added");
      setOpen(false); setName("");
      localStorage.setItem("stems_company", data.id);
      window.location.reload();
    } catch { toast.error("Failed to add company"); }
  };

  const openManage = () => { setEdits(Object.fromEntries(companies.map((c) => [c.id, c.name]))); setManageOpen(true); };
  const saveEdit = async (c) => {
    const nm = (edits[c.id] || "").trim();
    if (!nm) return;
    setSavingId(c.id);
    try {
      await api.put(`/companies/${c.id}`, { name: nm });
      toast.success("Business unit updated");
      await load();
    } catch { toast.error("Failed to update business unit"); }
    finally { setSavingId(null); }
  };

  const isCeo = isRole(ROLES.CEO);
  const canAllUnits = isCeo || isRole(ROLES.FM) || isRole(ROLES.GM) || isRole(ROLES.ACEO);
  const current = active === "all" ? { name: "All business units" } : (companies.find((c) => c.id === active) || companies[0]);
  if (companies.length === 0) return null;

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button data-testid="company-switcher" className="flex items-center gap-2 h-9 px-2 sm:px-3 rounded-lg bg-[#1C2541] border border-slate-700 text-sm text-white max-w-[110px] sm:max-w-[200px]">
            <Building2 className="h-4 w-4 text-sky-300 shrink-0" />
            <span className="truncate">{current?.name || "Company"}</span>
            <ChevronDown className="h-4 w-4 text-slate-400 shrink-0 hidden sm:block" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          {canAllUnits && companies.length > 1 && (
            <>
              <DropdownMenuItem data-testid="company-option-all" onClick={() => pick("all")}
                className={active === "all" ? "bg-slate-100 font-semibold" : ""}>
                <Layers className="h-4 w-4 mr-2 text-slate-400" /> All business units
              </DropdownMenuItem>
              <DropdownMenuSeparator />
            </>
          )}
          {companies.map((c) => (
            <DropdownMenuItem key={c.id} data-testid={`company-option-${c.id}`} onClick={() => pick(c.id)}
              className={c.id === active ? "bg-slate-100 font-semibold" : ""}>
              <Building2 className="h-4 w-4 mr-2 text-slate-400" /> {c.name}
            </DropdownMenuItem>
          ))}
          {isCeo && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem data-testid="manage-units" onClick={openManage} className="text-slate-700">
                <Pencil className="h-4 w-4 mr-2" /> Edit business units
              </DropdownMenuItem>
              <DropdownMenuItem data-testid="add-company" onClick={() => setOpen(true)} className="text-blue-700">
                <Plus className="h-4 w-4 mr-2" /> New business unit
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>New Business Unit</DialogTitle></DialogHeader>
          <Input data-testid="company-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. SilasTech Solar Division" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={create} data-testid="company-create-btn" className="bg-[#1E3A8A] hover:bg-[#1D4ED8]">Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={manageOpen} onOpenChange={setManageOpen}>
        <DialogContent className="max-w-md" data-testid="manage-units-dialog">
          <DialogHeader><DialogTitle>Edit Business Units</DialogTitle></DialogHeader>
          <div className="space-y-2 max-h-[60vh] overflow-y-auto">
            {companies.map((c) => (
              <div key={c.id} className="flex items-center gap-2" data-testid={`manage-unit-${c.id}`}>
                <Input value={edits[c.id] ?? ""} data-testid={`unit-name-${c.id}`}
                  onChange={(e) => setEdits((m) => ({ ...m, [c.id]: e.target.value }))} />
                <Button size="sm" data-testid={`unit-save-${c.id}`} disabled={savingId === c.id || !(edits[c.id] || "").trim()}
                  onClick={() => saveEdit(c)} className="bg-[#1E3A8A] hover:bg-[#1D4ED8] shrink-0">Save</Button>
              </div>
            ))}
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setManageOpen(false)}>Close</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
